import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import { useLanguage } from "../contexts/LanguageContext";

/**
 * Granalis, das größte Produkt der BROVKO GmbH.
 * Aufbau folgt der Produktlogik aus dem Projekt: Verlässlichkeitsproblem,
 * Kette von der Quelle zum geprüften Wissen, die vier Ebenen, Abgrenzung,
 * Rollen, Stärken, lebender Datenbestand.
 */

const seo = {
  title: "Granalis | Geprüftes Wissen aus unstrukturierten Daten | BROVKO",
  description:
    "Granalis macht aus großen Beständen unstrukturierter Daten einen strukturierten, belegten und vergleichbaren Datenbestand. Jede Aussage weiß, woher sie stammt und wie sicher sie ist.",
  keywords:
    "Granalis, Enterprise Knowledge Platform, unstrukturierte Daten, Dokumentenextraktion, Provenienz, KI prüft KI, Datenbestand",
  canonicalUrl: "/granalis",
};

/* Die vier Schritte mit den Ansichten aus der Anwendung selbst.
   Sie zeigen, was das Produkt an dieser Stelle tatsaechlich macht. */
const kette = [
  { n: "Schritt 1", t: "Erfassen",
    p: "PDF, Scan, Tabelle, E-Mail, Bild oder Meldung, im Format des Absenders. Jede Quelle wird angenommen, versioniert und ihrem Vorgang zugeordnet. Nichts geht verloren, nichts wird abgetippt.",
    bild: "/img/granalis/erfassen.jpg",
    alt: "Eingangsliste der Anwendung mit vier Quellen: PDF-Datenblatt, Scan einer Lieferantenerklärung, Preisliste als Tabelle und eine E-Mail mit drei Anhängen" },
  { n: "Schritt 2", t: "Verstehen",
    p: "Typ erkennen, Werte extrahieren, aus Text, Tabellen und Kennlinien, und jeden Wert in die Ontologie einordnen. Jeder Fundort wird mitgeliefert: Dokument, Seite, Position.",
    bild: "/img/granalis/verstehen.jpg",
    alt: "Extraktionsansicht: erkannte Betriebstemperatur, Fundstelle AEC-Q100 Seite 12, eine Kennlinie mit zwölf Punkten und die Einordnung in die Ontologie" },
  { n: "Schritt 3", t: "Prüfen",
    p: "KI prüft KI. Eine zweite Instanz bestätigt oder widerspricht. Wo abgewichen wird, entscheidet ein Fachprüfer. Was freigegeben ist, bleibt nachvollziehbar.",
    bild: "/img/granalis/pruefen.jpg",
    alt: "Prüfansicht: Extraktion, Gegenprüfung durch eine zweite Instanz, Abweichung bei der Kennlinie mit Review durch einen Fachprüfer, Status freigegeben" },
  { n: "Schritt 4", t: "Arbeiten",
    p: "Abfragen in Echtzeit, erkannte Muster, Alerts vor ablaufenden Zertifikaten und gezielte Aktionen zurück ins Zielsystem. Vollexport jederzeit.",
    bild: "/img/granalis/arbeiten.jpg",
    alt: "Ergebnisansicht: Abfrage mit 212 Treffern, abweichende Preisstaffeln bei drei Lieferanten, Alert zu einem ablaufenden Zertifikat und eine Aktion ins ERP" },
];

const ebenen = [
  { t: "Quelle", p: "Das Dokument, die E-Mail, der Datensatz. Bleibt unverändert und jederzeit nachschlagbar." },
  { t: "Observation", p: "Was aus dieser einen konkreten Quelle erkannt wurde. Noch keine Wahrheit, sondern eine Beobachtung." },
  { t: "Assertion", p: "Eine prüfbare Aussage über eine Entität oder eine Beziehung. Sie kann bestätigt, bestritten oder abgelöst werden." },
  { t: "Evidence", p: "Die konkrete Belegstelle: Dokument, Seite, Textstelle, Position. Der Beleg hängt an der Aussage, nicht am Dokument." },
];

const abgrenzung = [
  { t: "Kein Dokumentenmanagement", p: "Dokumente sind Quellen, nicht das Produkt." },
  { t: "Kein Chatbot", p: "Ein Assistent kann obendrauf sitzen. Er ist ein Nutzungskanal, nicht der Kern." },
  { t: "Kein reines OCR-Werkzeug", p: "Extraktion ist ein Schritt auf dem Weg, nicht das Ziel." },
  { t: "Kein Ersatz für ERP, CRM oder PLM", p: "Diese Systeme bleiben, was sie sind. Granalis setzt als eigene Schicht darüber." },
  { t: "Kein isoliertes RAG-System", p: "RAG kann ein Zugriffsweg sein. Entitätsauflösung, Konflikte, Zeitgültigkeit und Freigaben löst es nicht." },
  { t: "Und kein Versprechen auf Fehlerfreiheit", p: "Granalis verspricht keine fehlerfreie Extraktion und keine autonomen Entscheidungen. Es verspricht, dass Sie jede Aussage nachprüfen können." },
];

const rollen = [
  { t: "Geschäftsführung", p: "Geschwindigkeit, Kosten und Wissen, das nicht an einzelnen Personen hängt." },
  { t: "Operations", p: "Durchsatz, gleichbleibende Qualität und eine saubere Ausnahmebearbeitung." },
  { t: "CIO und CTO", p: "Integration in die bestehende Landschaft, Sicherheit, Architektur, kein Lock-in." },
  { t: "Head of Data und AI", p: "Belastbares Grounding, nachvollziehbare Herkunft, Wiederverwendbarkeit." },
  { t: "Fachbereich", p: "Weniger suchen, den Beleg direkt danebenstehen haben, schneller entscheiden." },
  { t: "Compliance und Security", p: "Nachvollziehbarkeit, Rollen, Audit-Trail und Datenresidenz." },
];

const staerken = [
  { t: "Wissensobjekte statt Textschnipsel", p: "Entitäten und Beziehungen, nicht Textfragmente ohne Zusammenhang." },
  { t: "Beleg auf Aussageebene", p: "Nicht das Dokument ist belegt, sondern jede einzelne Aussage darin. Das lässt sich nachträglich nicht einbauen." },
  { t: "Prüfung eingebaut", p: "Risikoabhängig statt pauschal. Der Normalfall läuft automatisch, die Ausnahme geht an einen Menschen." },
  { t: "Anbieterunabhängig", p: "Modelle und Cloud sind austauschbar. Die Architektur bindet Sie an keinen Anbieter." },
  { t: "Schrittweise Einführung", p: "Kein Systemaustausch. Ein Use Case zuerst, der Rest folgt, wenn er sich rechnet." },
  { t: "API-first", p: "Das Wissen ist von außen nutzbar und nicht in einer Oberfläche gefangen." },
];

const H2 = ({ children, className = "" }) => (
  <h2 className={`text-2xl font-extrabold tracking-tight sm:text-3xl ${className}`}>{children}</h2>
);

const Granalis = () => {
  useLanguage();

  return (
    <div className="theme-brand min-h-screen bg-surface text-ink">
      <SEOHead {...seo} />
      <Header />

      {/* Auftakt */}
      <section className="relative overflow-hidden pt-20 pb-24">
        <div className="absolute inset-0 bg-brand-hero" aria-hidden="true" />
        <div className="relative container mx-auto grid items-center gap-14 px-4 lg:grid-cols-[1.02fr_0.98fr]">
          <div>
          <span className="inline-flex items-center rounded-full border border-brand-400/25 bg-brand/10 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-300">
            Enterprise Knowledge Platform
          </span>
          <h1 className="mt-6 max-w-[16ch] text-[clamp(2.3rem,4.4vw,3.5rem)] font-extrabold leading-[1.06] tracking-[-0.035em] [text-wrap:balance]">
            Granalis liest, was bisher nur Menschen lesen konnten.{" "}
            <span className="text-brand-400">Und belegt jede Aussage.</span>
          </h1>
          <p className="mt-6 max-w-[52ch] text-base leading-relaxed text-ink-muted sm:text-[1.05rem]">
            Aus großen Beständen unstrukturierter Daten wird ein strukturierter, belegter und
            vergleichbarer Datenbestand. Nicht als einmaliger Export, sondern als Schicht, die
            dauerhaft weiterarbeitet.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-4">
            <Link
              to="/contact"
              className="inline-flex h-11 items-center gap-2 rounded-lg bg-brand px-6 text-sm font-bold text-ink-onBrand shadow-[0_10px_30px_-12px_rgba(20,184,166,.8)] transition-colors duration-200 hover:bg-brand-600"
            >
              Pilot besprechen
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="https://granalis.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-300 transition-colors hover:text-brand-400"
            >
              granalis.com
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
          </div>

          {/* Rechte Spalte. Auf 720px begrenzt: Bei 1500px wurde die Aufnahme von
              Bei voller Breite wurde die Aufnahme von nativ 1200px
              hochskaliert und damit weich, und sie war hoeher als das
              Sichtfenster. */}
          <figure className="lg:max-w-[720px]">
            <div className="overflow-hidden rounded-2xl border border-hairline bg-surface-raised shadow-[0_2px_4px_rgba(0,0,0,.35),0_28px_56px_-28px_rgba(0,0,0,.75)]">
              <img
                src="/img/granalis/erfassen.jpg"
                alt="Eingangsliste der Anwendung: PDF-Datenblatt, Scan einer Lieferantenerklärung, Preisliste als Tabelle und eine E-Mail mit drei Anhängen"
                className="w-full"
                width="940"
                height="266"
              />
            </div>
            <figcaption className="mt-4 max-w-[52ch] text-sm text-ink-faint">
              Der Eingang: jede Quelle im Format des Absenders, angenommen, versioniert und ihrem
              Vorgang zugeordnet.
            </figcaption>
          </figure>
        </div>
      </section>

      {/* Das Problem */}
      <section className="border-b border-hairline py-24">
        <div className="container mx-auto grid gap-14 px-4 lg:grid-cols-[1.1fr_0.9fr]">
          <H2 className="max-w-[14ch]">
            Unternehmen haben kein Informationsdefizit. Sie haben ein Verlässlichkeitsproblem.
          </H2>
          <div className="space-y-4 text-base leading-relaxed text-ink-muted">
            <p className="max-w-[56ch]">
              Die wichtigen Fakten stecken in PDFs, E-Mails, Tabellen und Fachsystemen. Sie sind
              unterschiedlich benannt, widersprechen sich, sind unterschiedlich alt. Niemand weiß
              ohne Nachprüfen, welche Version gilt.
            </p>
            <p className="max-w-[56ch]">
              Die Folge ist Alltag in fast jedem Unternehmen: Mitarbeiter suchen, vergleichen und
              übertragen dieselben Inhalte immer wieder von Hand.
            </p>
            <p className="max-w-[56ch]">
              Klassische Suche verschärft das eher, weil sie{" "}
              <b className="font-semibold text-ink">Dokumente liefert, keine Antworten</b>. Und wer
              generative KI direkt auf diesen Bestand setzt, bekommt plausibel klingende Antworten
              ohne belastbare Grundlage.
            </p>
            <p className="mt-9 max-w-[24ch] border-t-2 border-brand pt-7 text-xl font-bold leading-snug tracking-tight text-ink sm:text-2xl">
              Plausibilität ist nicht Verlässlichkeit.
            </p>
          </div>
        </div>
      </section>

      {/* Die Kette */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-[62ch]">
            <H2>Der Weg von der Quelle zum geprüften Wissen</H2>
            <p className="mt-5 text-base leading-relaxed text-ink-muted">
              Jedes Dokument durchläuft dieselbe Kette. Was am Ende im Bestand landet, hat sie
              vollständig passiert.
            </p>
          </div>

          {/* Bebilderte Zeilen: links die Ansicht aus der Anwendung, rechts
              die Erklaerung. Seiten wechseln, damit der Blick wandert. */}
          <div className="mt-14 border-t border-hairline">
            {kette.map((s, i) => (
              <div
                key={s.t}
                className="grid items-center gap-8 border-b border-hairline py-12 lg:grid-cols-2 lg:gap-16"
              >
                <figure className={`overflow-hidden rounded-2xl border border-hairline bg-surface-raised shadow-[0_2px_4px_rgba(0,0,0,.3),0_20px_44px_-24px_rgba(0,0,0,.7)] ${i % 2 ? "lg:order-2" : ""}`}>
                  <img
                    src={s.bild}
                    alt={s.alt}
                    className="w-full"
                    width="940"
                    height="280"
                    loading="lazy"
                  />
                </figure>
                <div className={i % 2 ? "lg:order-1" : ""}>
                  <div className="text-[11px] uppercase tracking-[0.16em] text-brand-300">{s.n}</div>
                  <h3 className="mt-3 text-[clamp(1.5rem,2.4vw,2.1rem)] font-extrabold tracking-[-0.03em]">
                    {s.t}
                  </h3>
                  <p className="mt-4 max-w-[54ch] text-[0.98rem] leading-relaxed text-ink-muted">
                    {s.p}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3.5 grid gap-3.5 md:grid-cols-2">
            <article className="rounded-2xl border border-hairline bg-surface-raised p-7">
              <div className="text-[11px] uppercase tracking-[0.16em] text-brand-300">Sicher genug</div>
              <p className="mt-3 text-sm leading-relaxed text-ink-muted">
                Die Aussage geht direkt in die Wissensschicht, mit Beleg, Zeitgültigkeit und
                Sicherheitsgrad.
              </p>
            </article>
            <article className="rounded-2xl border border-hairline bg-surface-raised p-7">
              <div className="text-[11px] uppercase tracking-[0.16em] text-amber-400">Nicht sicher genug</div>
              <p className="mt-3 text-sm leading-relaxed text-ink-muted">
                Ein Mensch entscheidet. Nicht pauschal bei allem, sondern gezielt dort, wo
                Unsicherheit oder Risiko hoch sind. Human in the Loop ist hier kein Kompromiss,
                sondern Produktmerkmal.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* Die vier Ebenen */}
      <section className="border-y border-hairline bg-surface-sunken py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch]">
            <H2>Vier Dinge, die andere Systeme vermischen</H2>
            <p className="mt-5 text-base leading-relaxed text-ink-muted">
              Das ist der fachliche Kern. Weil Granalis diese vier Ebenen sauber trennt, kann es
              nicht nur sagen, was vermutlich gilt, sondern auch: woher es stammt, wann es galt, wie
              sicher es ist, wer es geprüft hat und welche Quelle ihm widerspricht.
            </p>
          </div>
          <dl className="mt-12 border-t border-hairline">
            {ebenen.map((e) => (
              <div
                key={e.t}
                className="grid gap-3 border-b border-hairline py-6 transition-[background-color,padding] duration-300 ease-brand hover:bg-brand/[0.045] hover:pl-4 md:grid-cols-[210px_1fr] md:gap-9"
              >
                <dt className="text-base font-bold tracking-tight text-brand-300">{e.t}</dt>
                <dd className="max-w-[60ch] text-sm leading-relaxed text-ink-muted">{e.p}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Abgrenzung */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-[62ch]">
            <H2>Was Granalis ausdrücklich nicht ist</H2>
            <p className="mt-5 text-base leading-relaxed text-ink-muted">
              Diese Abgrenzung ist eine Stärke, keine Zurückhaltung. Sie verhindert die Erwartung,
              gegen die jedes Projekt dieser Art sonst verliert.
            </p>
          </div>
          <div className="mt-12 grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
            {abgrenzung.map((a) => (
              <article key={a.t} className="rounded-2xl border border-hairline bg-surface-raised p-7">
                <h3 className="text-[15px] font-bold tracking-tight">{a.t}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-ink-muted">{a.p}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Für wen */}
      <section className="border-y border-hairline bg-surface-sunken py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch]">
            <H2>Für wen es gebaut ist</H2>
            <p className="mt-5 text-base leading-relaxed text-ink-muted">
              Mittelständische bis große Unternehmen mit dokumentenintensiven Prozessen,
              fragmentierter Systemlandschaft und hohem Fachwissensanteil. Wer kauft und wer nutzt,
              sind dabei selten dieselben Personen.
            </p>
          </div>
          <dl className="mt-12 grid border-t border-hairline md:grid-cols-2 md:gap-x-14">
            {rollen.map((r) => (
              <div key={r.t} className="border-b border-hairline py-5">
                <dt className="text-base font-bold tracking-tight">{r.t}</dt>
                <dd className="mt-1.5 text-sm leading-relaxed text-ink-muted">{r.p}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Stärken */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <H2 className="max-w-[28ch]">Sechs Eigenschaften, die selten zusammen auftreten</H2>
          <div className="mt-12 grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
            {staerken.map((s, i) => (
              <article key={s.t} className="rounded-2xl border border-hairline bg-surface-raised p-7">
                <div className="text-2xl font-extrabold tabular-nums tracking-tight text-brand-400">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="mt-3 text-[15px] font-bold tracking-tight">{s.t}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-ink-muted">{s.p}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Lebender Datenbestand */}
      <section className="border-y border-hairline bg-surface-sunken py-24">
        <div className="container mx-auto grid gap-14 px-4 lg:grid-cols-[1.1fr_0.9fr]">
          <H2 className="max-w-[14ch]">Ihr Datenbestand arbeitet weiter.</H2>
          <div className="space-y-4 text-base leading-relaxed text-ink-muted">
            <p className="max-w-[56ch]">
              Granalis ist keine Durchlaufstation. Die Daten fließen hinein und bleiben. Was
              herausfließt, sind Ergebnisse: Antworten auf konkrete Fragen, erkannte Muster, Alerts
              und gezielte Rückschreibungen in das jeweilige Zielsystem.
            </p>
            <p className="max-w-[56ch]">
              Ein einmaliger Massenexport wäre nur eine alternde Momentaufnahme. Deshalb bewerben
              wir ihn nicht.
            </p>
            <p className="max-w-[56ch]">
              <b className="font-semibold text-ink">
                Vollexport und Datenmitnahme sind trotzdem jederzeit zugesagt.
              </b>{" "}
              Ihre Daten gehören Ihnen, und der Weg hinaus steht immer offen. Ohne diese Zusage
              würde kein CIO ernsthaft anfangen.
            </p>
          </div>
        </div>
      </section>

      {/* Abschluss */}
      <section className="py-24 text-center">
        <div className="container mx-auto px-4">
          <H2 className="mx-auto max-w-[24ch]">
            Der erste Schritt ist ein Use Case, nicht eine Plattformentscheidung.
          </H2>
          <p className="mx-auto mt-5 max-w-[58ch] text-base leading-relaxed text-ink-muted">
            Wir suchen gemeinsam einen Prozess, in dem sich Wirkung schnell nachrechnen lässt, und
            bauen ihn. Alles Weitere entscheiden Sie danach.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-4">
            <Link
              to="/contact"
              className="inline-flex h-11 items-center gap-2 rounded-lg bg-brand px-6 text-sm font-bold text-ink-onBrand shadow-[0_10px_30px_-12px_rgba(20,184,166,.8)] transition-colors duration-200 hover:bg-brand-600"
            >
              Pilot besprechen
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/produkte"
              className="inline-flex h-11 items-center gap-2 rounded-lg border border-hairline-strong px-6 text-sm font-bold transition-colors duration-200 hover:bg-white/[0.06]"
            >
              Alle Produkte ansehen
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Granalis;
